import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import ElectricityBillOutputComponent from '../output/ElectricityBillOutputComponent';

// Electricity operator code → readable name lookup
const OPERATOR_NAMES = {
    APS: 'APSPDCL – Andhra Pradesh Southern', APEPDCL: 'APEPDCL – Andhra Pradesh Eastern',
    APCPDCL: 'APCPDCL – Andhra Pradesh Central', APNPDCL: 'APNPDCL – Andhra Pradesh Northern',
    ASSAM: 'APDCL – Assam Power Distribution', APDCL: 'APDCL – Assam Power Distribution',
    BG: 'DHBVN – Dakshin Haryana Bijli Vitran', BS: 'BSES Surya – Delhi',
    BR: 'BSES Rajdhani – Delhi', BY: 'BSES Yamuna – Delhi',
    BRPL: 'BSES Rajdhani – Delhi', BYPL: 'BSES Yamuna – Delhi',
    CESC: 'CESC – Calcutta Electric Supply', CSPDCL: 'CSPDCL – Chhattisgarh State Power',
    DGVCL: 'DGVCL – Dakshin Gujarat Vij Company', DHBVN: 'DHBVN – Dakshin Haryana Bijli Vitran',
    DISCOM: 'DISCOM – Distribution Company', DNHDD: 'DNHDD – Dadra & Nagar Haveli',
    ENER: 'Torrent Power – Ahmedabad / Surat', GESCOM: 'GESCOM – Gulbarga Electricity Supply',
    GUVNL: 'GUVNL – Gujarat Urja Vikas Nigam', HESCOM: 'HESCOM – Hubli Electricity Supply',
    HPSEBL: 'HPSEBL – Himachal Pradesh State Electricity', JBVNL: 'JBVNL – Jharkhand Bijli Vitran Nigam',
    JDVVNL: 'JDVVNL – Jodhpur Vidyut Vitran Nigam', JKPDD: 'JKPDD – J&K Power Development',
    KESCO: 'KESCO – Kanpur Electric Supply Company', KPDCL: 'KPDCL – Karnataka Power Distribution',
    KSEB: 'KSEB – Kerala State Electricity Board', LANCO: 'Lanco – Amarkantak Power',
    MAHADISCOM: 'MAHADISCOM – Maharashtra State Electricity', MAHAVITARAN: 'MSEDCL – Maharashtra State Electricity',
    MESCOM: 'MESCOM – Mangalore Electricity Supply', MGVCL: 'MGVCL – Madhya Gujarat Vij Company',
    MSEDCL: 'MSEDCL – Maharashtra State Electricity', NBPDCL: 'NBPDCL – North Bihar Power Distribution',
    NESCO: 'NESCO – North Eastern Electricity Supply', NORTHBIHAR: 'NBPDCL – North Bihar Power',
    PGVCL: 'PGVCL – Paschim Gujarat Vij Company', PSPCL: 'PSPCL – Punjab State Power Corporation',
    PVVNL: 'PVVNL – Paschimanchal Vidyut Vitran Nigam', SBPDCL: 'SBPDCL – South Bihar Power Distribution',
    SOUTHBIHAR: 'SBPDCL – South Bihar Power', TANGEDCO: 'TANGEDCO – Tamil Nadu Generation & Distribution',
    TATA: 'Tata Power – Mumbai', TPCODL: 'TPCODL – TP Central Odisha Distribution',
    TPNODL: 'TPNODL – TP Northern Odisha Distribution', TPSODL: 'TPSODL – TP Southern Odisha Distribution',
    TPWODL: 'TPWODL – TP Western Odisha Distribution', TORRENT: 'Torrent Power – Ahmedabad / Surat',
    TSECL: 'TSECL – Tripura State Electricity Corporation', TSNPDCL: 'TSNPDCL – Telangana Northern Power',
    TSSPDCL: 'TSSPDCL – Telangana Southern Power', UHBVN: 'UHBVN – Uttar Haryana Bijli Vitran',
    UPCL: 'UPCL – Uttarakhand Power Corporation', UPPCL: 'UPPCL – Uttar Pradesh Power Corporation',
    WBSEDCL: 'WBSEDCL – West Bengal State Electricity', WESCOMP: 'WESCO – Western Electricity Supply Odisha',
};

const resolveOperatorLabel = (code, nameFromApi) => {
    const upper = String(code).toUpperCase();
    if (nameFromApi && nameFromApi !== code && nameFromApi.length > 2) return `${nameFromApi} (${code})`;
    return OPERATOR_NAMES[upper] ? `${OPERATOR_NAMES[upper]} (${code})` : code;
};

// Extract operators from various possible Surepass response shapes
const extractOperators = (responseData) => {
    const d = responseData?.data?.data?.data
        ?? responseData?.data?.data
        ?? responseData?.data
        ?? responseData;
    if (!d) return [];

    const toOption = (op) => {
        if (typeof op === 'object') {
            const code = op.operator_code || op.code || op.id || '';
            const name = op.operator_name || op.name || op.display_name || '';
            return { label: resolveOperatorLabel(code, name), value: code.toUpperCase() };
        }
        return { label: resolveOperatorLabel(String(op), ''), value: String(op).toUpperCase() };
    };

    if (Array.isArray(d.operator_list)) return d.operator_list.map(toOption).filter(o => o.value);
    if (Array.isArray(d.operators))     return d.operators.map(toOption).filter(o => o.value);
    if (Array.isArray(d.data))          return d.data.map(toOption).filter(o => o.value);
    if (Array.isArray(d))               return d.map(toOption).filter(o => o.value);

    // Fallback: first array-valued key
    const arrayEntry = Object.values(d).find((v) => Array.isArray(v) && v.length > 0);
    if (arrayEntry) return arrayEntry.map(toOption).filter(o => o.value);
    return [];
};

const ElectricityFlowComponent = () => {
    const { operatorListServiceId, billServiceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [operators, setOperators] = useState([]);
    const [loadingOperators, setLoadingOperators] = useState(true);
    const [operatorLoadError, setOperatorLoadError] = useState(false);

    const [operatorDropdown, setOperatorDropdown] = useState(null);
    const [operatorText, setOperatorText] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Load Electricity Bill service credits
    useEffect(() => {
        if (!billServiceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${billServiceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [billServiceId]);

    // Auto-fetch operator list on mount
    useEffect(() => {
        if (!operatorListServiceId) { setLoadingOperators(false); setOperatorLoadError(true); return; }
        setLoadingOperators(true);
        axiosInstance.get('/surepass/utility/electricity/operator-code-list', {
            params: { api_provider_service_id: parseInt(operatorListServiceId, 10) },
        })
            .then((r) => {
                const extracted = extractOperators(r.data);
                if (extracted.length > 0) {
                    setOperators(extracted);
                } else {
                    setOperatorLoadError(true);
                }
            })
            .catch(() => setOperatorLoadError(true))
            .finally(() => setLoadingOperators(false));
    }, [operatorListServiceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const rd = error.response?.data;
        let detail;
        if (s === 402) detail = `Insufficient credits. Required: ${rd?.required_credits ?? 'N/A'}, Available: ${rd?.current_balance ?? 'N/A'}`;
        else if (s === 422) { const e = rd?.errors; detail = e ? Object.values(e)[0]?.[0] : rd?.message || 'Validation failed'; }
        else if (s === 429) detail = 'Too many requests. Please wait before trying again.';
        else if (s === 404) detail = 'No bill found for this consumer ID.';
        else detail = rd?.message || error.message || summary;
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    const validate = () => {
        const next = {};
        const selectedOp = operatorDropdown || operatorText.trim().toUpperCase();
        if (!selectedOp) next.operatorCode = 'Operator code is required';
        if (!idNumber.trim()) next.idNumber = 'Consumer ID is required';
        if (!consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSubmit = async () => {
        setLoading(true);
        const operatorCode = operatorDropdown || operatorText.trim().toUpperCase();
        try {
            const res = await axiosInstance.post('/surepass/utility/electricity/bill', {
                operator_code: operatorCode,
                id_number: idNumber.trim(),
                api_provider_service_id: parseInt(billServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Lookup failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Bill fetched', detail: 'Report ready below', life: 2000 });
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${billServiceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (_) {}
        } catch (err) {
            showError(err, 'Electricity bill fetch failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        const operatorCode = operatorDropdown || operatorText.trim().toUpperCase();
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Operator:</span> <span className="text-900 font-medium font-mono">{operatorCode}</span></div>
                    <div className="mb-2"><span className="text-600">Consumer ID:</span> <span className="text-900 font-medium font-mono">{idNumber}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm electricity bill lookup',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: doSubmit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const reset = () => {
        setReportData(null);
        setOperatorDropdown(null);
        setOperatorText('');
        setIdNumber('');
        setConsent(false);
        setErrors({});
        submittingRef.current = false;
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-bolt"
                        title="Electricity Bill"
                        subtitle="Fetch electricity bill details by operator and consumer ID"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="operatorCode" className="block font-medium text-sm mb-2">
                                            Operator <span className="text-red-500">*</span>
                                        </label>
                                        {loadingOperators ? (
                                            <div className="flex align-items-center gap-2 p-2">
                                                <i className="pi pi-spinner pi-spin text-primary" />
                                                <span className="text-500 text-sm">Loading operators...</span>
                                            </div>
                                        ) : operators.length > 0 ? (
                                            <Dropdown
                                                id="operatorCode"
                                                value={operatorDropdown}
                                                options={operators}
                                                onChange={(e) => { setOperatorDropdown(e.value); if (errors.operatorCode) setErrors((p) => ({ ...p, operatorCode: '' })); }}
                                                placeholder="Select an operator"
                                                filter
                                                filterPlaceholder="Search operator"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.operatorCode })}
                                            />
                                        ) : (
                                            <IconField iconPosition="left" className="w-full">
                                                <InputIcon className="pi pi-bolt" />
                                                <InputText
                                                    id="operatorCode"
                                                    value={operatorText}
                                                    onChange={(e) => { setOperatorText((e.target.value || '').toUpperCase()); if (errors.operatorCode) setErrors((p) => ({ ...p, operatorCode: '' })); }}
                                                    placeholder="e.g. APSPDCL"
                                                    maxLength={30}
                                                    disabled={loading}
                                                    className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.operatorCode })}
                                                />
                                            </IconField>
                                        )}
                                        {operatorLoadError && !loadingOperators && <small className="text-orange-500 block mt-1"><i className="pi pi-exclamation-triangle mr-1" />Could not load operators — enter manually</small>}
                                        {errors.operatorCode && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.operatorCode}</small>}
                                    </div>

                                    <div className="field col-12 md:col-6 lg:col-5">
                                        <label htmlFor="consumerId" className="block font-medium text-sm mb-2">
                                            Consumer ID <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText
                                                id="consumerId"
                                                value={idNumber}
                                                onChange={(e) => { setIdNumber(e.target.value || ''); if (errors.idNumber) setErrors((p) => ({ ...p, idNumber: '' })); }}
                                                placeholder="e.g. 123456789012"
                                                inputMode="numeric"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.idNumber })}
                                            />
                                        </IconField>
                                        {errors.idNumber && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.idNumber}</small>}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={(e) => { setConsent(e.checked); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                                    error={errors.consent}
                                    reportType="electricity-bill"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Fetch Bill" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading || loadingOperators} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Fetch Another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={reset} />
                            </div>
                            <ElectricityBillOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ElectricityFlowComponent;
